import React from "react";
import { Stack, Typography, Button, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { PATH_DASHBOARD } from "@app/routes/paths";
import { useResponsive } from "@app/hooks";
import { imageSource } from "@app/constants";

interface RideBannerProps {
  title: string;
  subtitle: string;
  compareLabels: string;
  yourRide: string;
  description: string;
  buttonLabel: string;
  image: string;
  onClick: () => void;
}

const RideCards = ({
  title,
  compareLabels,
  yourRide,
  subtitle,
  description,
  buttonLabel,
  image,
  onClick,
}: RideBannerProps) => {
  const isMobile = useResponsive("down", "md");
  const navigate = useNavigate();
  const theme = useTheme();

  const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
      fontWeight="bold"
      width={{ xs: "100%", sm: 400, md: 500 }}
      fontSize={{ xs: 28, sm: 36, md: 50 }}
      fontFamily="Work Sans"
      sx={{
        lineHeight: 1.1,
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Stack
      sx={{
        minHeight: { xs: 380, md: 600 },
        height: "100vh",
        maxHeight: { xs: 450, md: 700 },
        width: { xs: "100%", xl: "70%" },
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-around",
        alignSelf: "center",
      }}
    >
      <Stack
        sx={{
          width: { xs: "100%", sm: "50%", md: "50%", xl: "40%" },
          alignItems: { xs: "", lg: "center" },
        }}
      >
        <Stack
          flex={1}
          textAlign={{ xs: "center", md: "left" }}
          display="flex"
          flexDirection="column"
          alignItems={{ xs: "center", md: "flex-start" }}
          px={{ xs: 1, md: 4 }}
        >
          <Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Heading>
                {title}{" "}
                <Typography
                  component="span"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: 28, sm: 36, md: 50 },
                  }}
                >
                  {compareLabels}
                </Typography>
              </Heading>
            </Stack>

            <Heading>
              {yourRide}{" "}
              <Typography
                component="span"
                fontWeight="bold"
                position={"relative"}
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: { xs: 28, sm: 36, md: 40 },
                }}
              >
                {subtitle}
                <Stack
                  component="img"
                  src={imageSource.easlyName}
                  alt="Easily logo"
                  sx={{
                    display: { xs: "none", sm: "block" },
                    position: "absolute",
                    bottom: { xs: -20, sm: -30 },
                    left: { xs: "80%", sm: "72%", md: "63%" },
                    transform: "translateX(-50%) scale(1.6)",
                    width: { xs: 40, sm: 50, md: 55 },
                    height: { xs: 40, sm: 50, md: 55 },
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              </Typography>
            </Heading>
          </Stack>
          <Typography
            color={theme.palette.grey[700]}
            width={{ xs: "100%", sm: 455 }}
            sx={{
              fontSize: { xs: 18, sm: 20, md: 22 },
              textAlign: { xs: "center", sm: "center", md: "left" },
              fontFamily: "Work Sans",
            }}
          >
            {description}
          </Typography>
          <Button
            variant="outlined"
            sx={{
              mt: 2,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
              },
              width: { xs: "100%", sm: "auto" },
              maxWidth: 250,
              mx: { xs: "auto", sm: 0 },
            }}
            onClick={onClick}
          >
            {buttonLabel}
          </Button>
        </Stack>
      </Stack>
      {!isMobile && (
        <Stack
          flex={1}
          alignItems={{ xs: "flex-start", xl: "flex-end" }}
          justifyContent="center"
          sx={{
            overflow: { xs: "hidden", xl: "visible" },
          }}
        >
          <img
            src={image}
            alt="ride"
            loading="lazy"
            style={{
              maxWidth: 750,
              aspectRatio: "1/1",
              objectFit: "contain",
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export { RideCards };
